import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Colors } from '@silvergatedelivery/constants';
import Loading from 'components/Loading';
import GroupPurchasesTable from './components/GroupPurchasesTable';
import { getDeliveryStaffIdSchema } from 'forms/schemas';
import {
  getOrders,
} from './helpers';
import { request } from 'utilities/graph';
import { adminUpdateOrder } from 'graphql/mutations';
import cache from 'utilities/cache';
import OrderTable from 'components/OrderTable';
import moment from 'moment-timezone';
import RestaurantSelect from './components/RestaurantSelect';
import OrderOverview from './components/OrderOverview';
import { toastr } from 'react-redux-toastr';

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    backgroundColor: Colors.background.light,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

export default function GroupPurchases({ clientId: inClientId }) {
  const classes = useStyles();
  const [lunchData, setLunchData] = useState([]);
  const [dinnerData, setDinnerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().add(4, 'day').format('YYYY-MM-DD'));
  const [targetDate, setTargetDate] = useState(moment().format('YYYY-MM-DD'));
  const [progressDate, setProgressDate] = useState('');
  const [deliveryStaffs, setDeliveryStaffs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ordersByDate, setOrdersByDate] = useState({});
  const [displayOrderIds, setDisplayOrderIds] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState();
  const [restaurantSelected, setRestaurantSelected] = useState();
  const [showClientCancelledOrders, setShowClientCancelledOrders] = useState(false);
  const [showAcceptedOrders, setShowAcceptedOrders] = useState(true);
  const [showPendingOrders, setShowPendingOrders] = useState(true);
  const [showAbandonedOrders, setShowAbandonedOrders] = useState(true);
  const clientId = inClientId || cache.get('app:facilityId');

  const setCustomPeriod = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const processOrders = (orders, allDeliveryStaffs) => {
    const currentDate = moment(startDate);
    const lastDate = moment(endDate);
    const ordersByDate = {};
    while (currentDate.isSameOrBefore(lastDate)) {
      const dateStr = currentDate.format('YYYY-MM-DD');
      ordersByDate[dateStr] = { lunch: [], dinner: [] };
      currentDate.add(1, 'day');
    }
    orders.forEach((order) => {
      const date = order.date;
      const mealSlot = order.mealSlot;
      ordersByDate[date][mealSlot].push(order);
    });
    setOrdersByDate(ordersByDate);

    const targetOrders = orders.filter(({ date }) => date === targetDate)
      .filter((order) => {
        if (order.groupPurchaseStatus === 'clientCancelled' && !showClientCancelledOrders) {
          return false;
        }
        if (order.groupPurchaseStatus === 'accepted' && !showAcceptedOrders) {
          return false;
        }
        if (order.groupPurchaseStatus === 'pending' && !showPendingOrders) {
          return false;
        }
        if (order.groupPurchaseStatus === 'abandoned' && !showAbandonedOrders) {
          return false;
        }
        return true;
      });
    const lunchTargetOrders = targetOrders.filter(({ mealSlot }) => mealSlot === 'lunch');
    const dinnerTargetOrders = targetOrders.filter(({ mealSlot }) => mealSlot === 'dinner');

    const processTargetOrders = (targetOrders) => {
      const orderDeliveryStaffList = [];
      const data = [];

      if (targetOrders.some(({ deliveryStaffId }) => !deliveryStaffId)) {
        orderDeliveryStaffList.push({
          id: 'N/A',
          name: '未指定送餐大使',
        });
      }
      targetOrders.forEach(({ deliveryStaffId }) => {
        if (deliveryStaffId) {
          if (orderDeliveryStaffList.findIndex((orderDeliveryStaff) => orderDeliveryStaff.id === deliveryStaffId) === -1) {
            const deliveryStaff = allDeliveryStaffs.find(({ id }) => id === deliveryStaffId);
            if (!deliveryStaff) {
              orderDeliveryStaffList.push({
                id: deliveryStaffId,
                name: '已停用或停止合作的大使',
              });
            }
          }
        }
      });
      targetOrders.forEach(({ deliveryStaffId }) => {
        if (deliveryStaffId) {
          if (orderDeliveryStaffList.findIndex((orderDeliveryStaff) => orderDeliveryStaff.id === deliveryStaffId) === -1) {
            const deliveryStaff = allDeliveryStaffs.find(({ id }) => id === deliveryStaffId);
            if (deliveryStaff) {
              orderDeliveryStaffList.push(deliveryStaff);
            }
          }
        }
      });
      let rowIndex = 0;
      targetOrders.forEach((order) => {
        order.mealItems.forEach((mealItem) => {
          const index = data.findIndex(({ restaurantId, mealItemName }) => {
            return restaurantId === order.restaurantId && mealItem.name === mealItemName;
          });
          let row;
          if (index === -1) {
            const deliveryStaffMealCountList = orderDeliveryStaffList.map(({ name, id }) => ({
              id,
              name,
              count: 0,
              orderIds: [],
              elders: [],
            }));
            row = {
              index: rowIndex,
              restaurantId: order.restaurantId,
              restaurantName: order.restaurant.name,
              mealItemName: mealItem.name,
              deliveryStaffMealCountList,
              total: 0,
              orderIds: [],
            };
            rowIndex += 1;
            data.push(row);
          } else {
            row = data[index];
          }
          const deliveryStaffIndex = row.deliveryStaffMealCountList.findIndex(({ id }) => {
            return order.deliveryStaffId ? id === order.deliveryStaffId : id === 'N/A';
          });
          row.total += mealItem.quantity;
          row.deliveryStaffMealCountList[deliveryStaffIndex].count += mealItem.quantity;
          if (!row.orderIds.includes(order.id)) {
            row.orderIds.push(order.id);
          }
          if (!row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.includes(order.id)) {
            row.deliveryStaffMealCountList[deliveryStaffIndex].orderIds.push(order.id);
            row.deliveryStaffMealCountList[deliveryStaffIndex].elders.push({
              ...order.elder,
              mealCount: mealItem.quantity,
              groupPurchaseStatus: order.groupPurchaseStatus,
              orderId: order.id,
            });
          }
        });
      });
      return data;
    };
    const lunchData = processTargetOrders(lunchTargetOrders);
    const dinnerData = processTargetOrders(dinnerTargetOrders);
    setLunchData(lunchData);
    setDinnerData(dinnerData);
  };

  useEffect(() => {
    if (!clientId) return;
    if (!restaurantSelected) return;
    (async () => {
      setIsLoading(true);

      const onUpdateLastItem = (item, nextToken) => {
        if (nextToken) {
          if (item) {
            setProgressDate(item.date);
          }
        } else {
          setProgressDate('');
        }
      };

      const orders = (await getOrders(restaurantSelected.id, startDate, endDate, onUpdateLastItem));

      let allDeliveryStaffs = deliveryStaffs;
      if (deliveryStaffs.length === 0) {
        // TODO: 有可能會少掉已經停用的大使
        allDeliveryStaffs = await getDeliveryStaffIdSchema(null, null, clientId,
          true, true, true, true, false);
        setDeliveryStaffs(allDeliveryStaffs);
      }
      setOrders(orders);
      if (!moment(targetDate).isBetween(startDate, endDate, 'day', '[]')) {
        setTargetDate(startDate);
      } else {
        processOrders(orders, allDeliveryStaffs);
      }
      setIsLoading(false);
    })();
  }, [restaurantSelected, startDate, endDate, clientId, lastUpdatedAt]);

  useEffect(() => {
    processOrders(orders, deliveryStaffs);
  }, [targetDate, showClientCancelledOrders, showAcceptedOrders, showPendingOrders, showAbandonedOrders]);

  const onOrdersAccept = async ({ orderIds }) => {
    const toUpdateOrders = orders.filter(({ id, groupPurchaseStatus }) => {
      return orderIds.includes(id) && groupPurchaseStatus !== 'accepted' && groupPurchaseStatus !== 'clientCancelled';
    });
    if (toUpdateOrders.length === 0) {
      toastr.error('沒有可接單的訂單');
      return;
    }
    const confirmMessage = `確定要接受這${toUpdateOrders.length}筆訂單嗎？`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    const data = toUpdateOrders.map((order) => (
      {
        id: order.id,
        restaurantId: order.restaurantId,
        deliveryBy: order.deliveryBy,
        status: order.deliveryStaffId ? 'ready' : 'waitingForDeliveryStaff',
        tier: order.tier,
        groupPurchaseStatus: 'accepted',
      }
    ));
    try {
      await request(adminUpdateOrder, { input: { orders: data } });
      toastr.success('訂單接單成功');
      const newOrders = orders.map((order) => {
        if (orderIds.includes(order.id)) {
          return { ...order, groupPurchaseStatus: 'accepted' };
        }
        return order;
      });
      setOrders(newOrders);
      processOrders(newOrders, deliveryStaffs);
    } catch (e) {
      console.log(e);
      if (e.errors && e.errors[0] && e.errors[0].message) {
        toastr.error(e.errors[0].message);
      }
    }
  };

  const onOrdersAbandon = async ({ orderIds }) => {
    const toUpdateOrders = orders.filter(({ id, groupPurchaseStatus }) => {
      return orderIds.includes(id) && groupPurchaseStatus !== 'abandoned' && groupPurchaseStatus !== 'clientCancelled';
    }).filter((order) => !['completed', 'delivered', 'cancelled'].includes(order.status));

    if (toUpdateOrders.length === 0) {
      toastr.error('沒有可拒單的訂單');
      return;
    }
    const confirmMessage = `確定要拒絕這${toUpdateOrders.length}筆訂單嗎？`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    const data = toUpdateOrders.map((order) => (
      {
        id: order.id,
        restaurantId: order.restaurantId,
        deliveryBy: order.deliveryBy,
        status: 'cancelled',
        tier: order.tier,
        groupPurchaseStatus: 'abandoned',
        cancellationReason: 'others',
        cancellationNote: '機構拒單',
      }
    ));
    try {
      await request(adminUpdateOrder, { input: { orders: data } });
      toastr.success('訂單拒單成功');
      const newOrders = orders.map((order) => {
        if (orderIds.includes(order.id)) {
          return { ...order, groupPurchaseStatus: 'abandoned' };
        }
        return order;
      });
      setOrders(newOrders);
      processOrders(newOrders, deliveryStaffs);
    } catch (e) {
      console.log(e);
      if (e.errors && e.errors[0] && e.errors[0].message) {
        toastr.error(e.errors[0].message);
      }
    }
  };

  return (
    <Container maxWidth={false} className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper style={{ padding: 16 }}>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <RestaurantSelect onUpdate={(value) => {
                      setRestaurantSelected(value);
                      setLastUpdatedAt(Date.now());
                    }}/>
                  </Grid>
                  <Grid item xs={9}>
                    <Grid container spacing={2} justifyContent="flex-start">
                      <Grid item xs={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showAcceptedOrders}
                              onChange={(e) => setShowAcceptedOrders(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="顯示已接單的訂單"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showPendingOrders}
                              onChange={(e) => setShowPendingOrders(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="顯示未接單的訂單"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showAbandonedOrders}
                              onChange={(e) => setShowAbandonedOrders(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="顯示已拒單的訂單"
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showClientCancelledOrders}
                              onChange={(e) => setShowClientCancelledOrders(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="顯示客戶退單的訂單"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {restaurantSelected &&
                <Grid item xs={12}>
                  <OrderOverview
                    ordersByDate={ordersByDate}
                    targetDate={targetDate}
                    setTargetDate={setTargetDate}
                    startDate={startDate}
                    setCustomPeriod={setCustomPeriod}
                  />
                </Grid>
              }
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {restaurantSelected &&
      <Grid container>
        {isLoading &&
          <>
            <Loading fullScreen={false} />
            <Typography variant="body1" color="textPrimary" style={{ flex: 1, textAlign: 'center' }}>
              {progressDate && `讀取進度：${progressDate}，請稍候...`}
            </Typography>
          </>}
        {!isLoading &&
          <Grid item xs={12}>
            <GroupPurchasesTable
              title='午餐訂單'
              data={lunchData}
              onOrdersAccept={onOrdersAccept}
              onOrdersAbandon={onOrdersAbandon}
              onCellClick={(value) => setDisplayOrderIds(value?.orderIds || [])}
              onRefresh={() => {
                setLastUpdatedAt(Date.now());
              }}
            />
          </Grid>
        }
        {!isLoading &&
          <Grid item xs={12}>
            <GroupPurchasesTable
              title='晚餐訂單'
              data={dinnerData}
              onOrdersAccept={onOrdersAccept}
              onOrdersAbandon={onOrdersAbandon}
              onCellClick={(value) => setDisplayOrderIds(value?.orderIds || [])}
              onRefresh={() => {
                setLastUpdatedAt(Date.now());
              }}
            />
          </Grid>
        }
        {displayOrderIds.length !== 0 &&
          <Grid item xs={12}>
            <OrderTable title='餐點訂單' orderIds={displayOrderIds} />
          </Grid>
        }
      </Grid>}
    </Container>
  );
}

GroupPurchases.propTypes = {
  clientId: PropTypes.string,
};
